import { useMemo } from 'react';
import { ExpenseCategory } from '@/types/database/expense-categories';

interface CategoryWithPath extends ExpenseCategory {
  hierarchyPath: string;
}

export const useCategoryHierarchy = (categories: ExpenseCategory[] | undefined) => {
  // Filter to only recordable categories (cost_position level)
  const recordableCategories = useMemo(() => {
    if (!categories) return [];
    return categories.filter(c => c.is_recordable);
  }, [categories]);

  // Build hierarchy path for each recordable category
  const categoriesWithPath = useMemo((): CategoryWithPath[] => {
    if (!categories || !recordableCategories.length) return [];

    const categoryMap = new Map(categories.map(c => [c.id, c]));

    return recordableCategories.map(category => {
      const pathParts: string[] = [category.name];
      let current = category;

      // Walk up the tree to build the full path
      while (current.parent_id) {
        const parent = categoryMap.get(current.parent_id);
        if (parent) {
          pathParts.unshift(parent.name);
          current = parent;
        } else {
          break;
        }
      }

      return {
        ...category,
        hierarchyPath: pathParts.join(' > '),
      };
    });
  }, [categories, recordableCategories]);

  // Group categories by their top-level parent (expense_category)
  const groupedCategories = useMemo(() => {
    if (!categories || !categoriesWithPath.length) return new Map<string, CategoryWithPath[]>();

    const categoryMap = new Map(categories.map(c => [c.id, c]));
    const groups = new Map<string, CategoryWithPath[]>();

    categoriesWithPath.forEach(category => {
      // Find the top-level parent (expense_category)
      let current: ExpenseCategory = category;
      while (current.parent_id) {
        const parent = categoryMap.get(current.parent_id);
        if (parent) {
          current = parent;
        } else {
          break;
        }
      }

      const groupName = current.name;
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(category);
    });

    return groups;
  }, [categories, categoriesWithPath]);

  return {
    recordableCategories,
    categoriesWithPath,
    groupedCategories,
  };
};
